/* eslint-disable stylistic/comma-dangle */
/**
 * King Living MegaMenu Theme
 *
 * Uses variant system for consistent styling with Header:
 * - variant="light": Black nav items (for transparent, solid states)
 * - variant="dark": White nav items (for gradient state)
 *
 * @override Extends Chakra UI theme
 */

import {A11Y_FOCUS_VISIBLE} from '@amblique/king-living/app/theme/foundations/a11y-focus-visible';
import {A11Y_ICON_GLYPH_SIZE, A11Y_ICON_TARGET_MIN} from '@amblique/king-living/app/theme/foundations/a11y-targets';
import {NAV_MOBILE_L2_SLIDE_MS} from '@amblique/king-living/app/constants/navigation-drawer';

/**
 * Get variant-specific styles for nav items
 * @param {string} color - Color token for nav items
 * @returns {Object} Variant styles
 */
const getNavVariantStyles = (color) => ({
  desktopTriggerButton: {
    color,
  },
  primaryTab: {
    color,
  },
});

/** Mouse click must not leave a ring; keyboard uses shared A11Y_FOCUS_VISIBLE. */
const noMouseFocusRing = {
  borderRadius: 0,
  outline: 'none',
  ...A11Y_FOCUS_VISIBLE,
  _active: {
    bg: 'transparent',
  },
};

const MegaMenu = {
  parts: [
    // Desktop styles
    'megaMenuDesktopWrapper',
    'megaMenuDesktopNav',
    'menuItemDesktopContainer',
    'desktopTriggerButton',
    'desktopPopoverContent',
    'megaMenuLoadingSpinner',
    'saleBadge',
    'primaryTabs',
    'primaryTab',
    'primaryTabActive',
    'primaryTabInactive',
    'drawer',
    'drawerChrome',
    'drawerSearchTab',
    'drawerSearchTabActive',
    'drawerClose',
    'drawerBody',
    'drawerL1Column',
    'drawerL1Scroll',
    'drawerL1',
    'drawerL1Item',
    'drawerL1Button',
    'drawerL1ButtonSelected',
    'drawerL1ButtonMuted',
    'drawerPromoGroup',
    'drawerPromoLink',
    'drawerL2',
    'drawerShopAll',
    'drawerL2Column',
    'drawerL2ColumnTitle',
    'drawerL2LinkGroup',
    'drawerL2Link',
    'drawerGallery',
    'drawerGalleryItem',
    'drawerGalleryCaption',
    'drawerSearchBody',
    // Mobile styles
    'mobileDrawerHeader',
    'mobileDrawerHeaderL2',
    'mobileDrawerTitle',
    'mobileDrawerOverlay',
    'mobileDrawerContent',
    'closeButton',
    'accountNameBadge',
    'logoMobileWrapper',
    'mobileDrawerLogoButton',
    'logoMobile',
    'mobileSearch',
    'mobileSearchInput',
    'mobileSearchIcon',
    'mobileTabsRow',
    'mobileMenuItemText',
    'mobilePromoGroup',
    'mobilePromoLink',
    'mobileCtaFooter',
    'mobileCtaRow',
    'mobileCtaExtraRow',
    'mobileCtaButton',
    'mobileCtaTypedLink',
    'mobileCtaExtraLink',
    'mobileCtaAccountBadge',
    'mobileDrawerBody',
    'mobileDrawerBodyInner',
    'mobileL1Slide',
    'mobileL1Panel',
    'mobileSubcategoryPanel',
    'mobileSubcategoryPanelContent',
    'mobileSubcategoryPanelWrapper',
    'mobileSubcategoryPanelWrap',
    'mobileSubmenuItemsWrapper',
    'mobileSubmenuItem',
    'mobilePanelItemsList',
    'backButtonIcon',
    'shopAllTitleIcon',
    'mobileDrawerFooter',
    'mobileDrawerFooterStack',
    'mobileDrawerFooterRow',
    'mobileMenuItemParentWrapper',
    'mobileDrawerFooterIconButton',
    'mobileDrawerLocaleSelector',
    'backButton',
    'backButtonWrapper',
    'menuItemIconButton',
    // Shared styles
    'menuItemLink',
    'menuItemButton',
    'menuItemText',
    'menuItemContainer',
    'menuItemIcon',
    'menuItemImages',
    'menuItemDesktopGrid',
    'menuItemDesktopImage',
    'navWrapper',
    'navigationColumnWrapper',
    'navigationColumnLinksWrapper',
    'navigationColumnTitle',
    'navigationColumnTitleSkeleton',
    'navigationColumnLinkSkeleton',
    'shopAllLinkWrapper',
  ],

  baseStyle: (props = {}) => {
    const {level = 0, isDesktop, isOpen, isSearchOpen, popoverTop, headerState, isLegacyNav} = props;

    // Level-based text styles
    const getTextStyles = () => {
      switch (level) {
        case 0:
          return {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '20px',
            fontWeight: '500',
            lineHeight: 1.2,
          };
        case 1:
          return {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '18px',
            fontWeight: '500',
            lineHeight: 1.2,
          };
        default:
          return {
            fontSize: '20px',
            fontWeight: '500',
          };
      }
    };

    return {
      // Desktop styles
      megaMenuDesktopWrapper: isLegacyNav ?
        {
          width: '100%',
        } :
        {
          height: 0,
          overflow: 'visible',
          width: 0,
        },
      primaryTabs: {
        alignItems: 'center',
        columnGap: {base: '16px', lg: '24px'},
        display: 'flex',
        sx: {
          '.header-primary-nav__item--active::after, .header-primary-nav__item--active:hover::after': {
            bg: 'currentColor',
            content: '""',
            height: '2px',
          },
        },
      },
      primaryTab: {
        ...noMouseFocusRing,
        color: 'inherit',
        cursor: 'pointer',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: {base: '14px', lg: '16px'},
        fontWeight: 500,
        height: {base: 'auto', lg: '32px'},
        lineHeight: 1.4,
        minH: {base: '44px'},
        overflow: 'visible',
        px: 0,
        py: 0,
        position: 'relative',
        textAlign: 'left',
        textDecoration: 'none',
        transition: 'opacity 0.2s ease',
        _after: {
          bg: 'transparent',
          borderRadius: 0,
          bottom: 0,
          content: '""',
          height: '2px',
          left: 0,
          position: 'absolute',
          right: 0,
          transition: 'background-color 0.2s ease',
        },
        _hover: {
          textDecoration: 'none',
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
        },
      },
      primaryTabActive: {
        borderBottom: 'none',
        borderRadius: 0,
        opacity: 1,
        _after: {
          bg: 'currentColor',
          borderRadius: 0,
          bottom: 0,
          content: '""',
          height: '2px',
          left: 0,
          position: 'absolute',
          right: 0,
        },
        _hover: {
          _after: {
            bg: 'currentColor',
          },
        },
      },
      primaryTabInactive: {
        opacity: 0.4,
        _hover: {
          opacity: 1,
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          opacity: 1,
        },
      },
      drawer: {
        bg: 'brandColors.primaryBG',
        bottom: 0,
        boxShadow: 'none',
        color: 'brandColors.primaryText90',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100dvh - var(--nav-drawer-top, var(--header-height, 72px)))',
        left: 0,
        minH: 0,
        overflow: 'hidden',
        position: 'fixed',
        top: 'var(--nav-drawer-top, var(--header-height, 72px))',
        transition: 'width 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
        zIndex: 1600,
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
        sx: {
          '@keyframes navDrawerChevronIn': {
            '0%': {opacity: 0, transform: 'translateX(-6px)'},
            '100%': {opacity: 1, transform: 'translateX(0)'},
          },
          '@keyframes navL1FadeIn': {
            '0%': {opacity: 0},
            '100%': {opacity: 1},
          },
          '@keyframes navL2CrossFade': {
            '0%': {opacity: 0},
            '100%': {opacity: 1},
          },
          '@keyframes navL3TileIn': {
            '0%': {opacity: 0, transform: 'translateY(14px)'},
            '100%': {opacity: 1, transform: 'translateY(0)'},
          },
          '@keyframes navL3TileFade': {
            '0%': {opacity: 0},
            '100%': {opacity: 1},
          },
          '.mega-menu-drawer__lock': {
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            height: '100%',
            minHeight: '0 !important',
            overflow: 'hidden',
            width: '100%',
          },
          '.mega-menu-drawer__gallery > .mega-menu-drawer__gallery-item': {
            transition: 'opacity 150ms ease',
          },
          '.mega-menu-drawer__gallery:hover > .mega-menu-drawer__gallery-item, .mega-menu-drawer__gallery:focus-within > .mega-menu-drawer__gallery-item': {
            opacity: '0.4 !important',
          },
          '.mega-menu-drawer__gallery:hover > .mega-menu-drawer__gallery-item:hover, .mega-menu-drawer__gallery:focus-within > .mega-menu-drawer__gallery-item:focus-visible': {
            opacity: '1 !important',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '.mega-menu-drawer__gallery > .mega-menu-drawer__gallery-item': {
              transition: 'none',
            },
          },
          ul: {
            listStyle: 'none',
            margin: 0,
          },
          'ul li': {
            listStyle: 'none',
          },
          '.header-primary-nav__item': {
            borderRadius: '0',
            color: 'brandColors.primaryText90',
            height: '32px',
            minHeight: 'unset',
            minWidth: 'unset',
            outline: 'none',
            paddingInlineEnd: '0',
            paddingInlineStart: '0',
            outlineOffset: '0',
            boxShadow: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderTop: 'none',
            outline: 'none',
            textAlign: 'left',
            '&:focus-visible': A11Y_FOCUS_VISIBLE._focusVisible,
          },
          'a:focus-visible, button:focus-visible, [tabindex="0"]:focus-visible':
            A11Y_FOCUS_VISIBLE._focusVisible,
          '.mega-menu-drawer__l1 .chakra-button': {
            fontSize: '20px',
            fontWeight: '500',
            height: 'auto',
            lineHeight: '1.2',
            minHeight: 'auto',
            minWidth: 'unset',
            paddingInlineEnd: '0',
            paddingInlineStart: '0',
            textDecoration: 'none',
          },
          '.mega-menu-drawer__l1 .chakra-button span, .mega-menu-drawer__l1 .chakra-button .chakra-text': {
            fontFamily: 'inherit',
            fontSize: '20px',
            fontWeight: '500',
            letterSpacing: '0',
            lineHeight: '1.2',
          },
          '.mega-menu-drawer__search-tab': {
            alignItems: 'center',
            display: 'flex',
            height: '32px',
            justifyContent: 'center',
            minHeight: 'unset',
            minWidth: 'unset',
            padding: '0',
            width: '32px',
          },
          '.mega-menu-drawer__search-tab svg': {
            height: '20px !important',
            width: '20px !important',
          },
          '.mega-menu-drawer__close': {
            alignItems: 'center',
            color: 'brandColors.primaryText',
            display: 'inline-flex',
            height: '32px',
            justifyContent: 'center',
            minHeight: 'unset',
            minWidth: 'unset',
            padding: '0',
            width: '32px',
          },
          '.mega-menu-drawer__close svg': {
            color: 'brandColors.primaryText',
            height: '20px',
            width: '20px',
          },
          '.mega-menu-drawer__l1-chevron': {
            color: 'brandColors.greyscale',
            flexShrink: 0,
            height: '11.25px',
            opacity: '0',
            width: '5.47px',
          },
          '.mega-menu-drawer__l1-chevron path': {
            opacity: '1',
          },
          '.mega-menu-drawer__l1 .chakra-button:hover .mega-menu-drawer__l1-chevron, .mega-menu-drawer__l1 .chakra-button:focus-visible .mega-menu-drawer__l1-chevron, .mega-menu-drawer__l1 .chakra-button[aria-expanded="true"] .mega-menu-drawer__l1-chevron': {
            color: 'brandColors.greyscale',
            opacity: '1',
            transform: 'translateX(0)',
          },
          '.mega-menu-drawer__shop-all': {
            alignItems: 'center',
            display: 'inline-flex',
            fontFamily: 'body',
            fontSize: '20px',
            fontWeight: '500',
            gap: '12px',
            height: '36px',
            letterSpacing: '0',
            lineHeight: '1.2',
            width: 'max-content',
          },
          '.mega-menu-drawer__shop-all span': {
            fontSize: '20px',
            fontWeight: '500',
            letterSpacing: '0',
            lineHeight: '1.2',
          },
          '.mega-menu-drawer__shop-all:hover > span:first-of-type, .mega-menu-drawer__shop-all:focus-visible > span:first-of-type': {
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          },
          '.mega-menu-drawer__l2 a:not(.mega-menu-drawer__shop-all):hover, .mega-menu-drawer__l2 a:not(.mega-menu-drawer__shop-all):focus-visible': {
            textDecoration: 'none',
            textUnderlineOffset: '3px',
          },
          '.mega-menu-drawer__shop-all svg': {
            display: 'block',
            flexShrink: 0,
            height: '16px',
            width: '16px',
          },
          '.mega-menu-drawer__promo a, .mega-menu-drawer__promo span': {
            alignItems: 'center',
            color: 'brandColors.itemMenu',
            display: 'flex',
            fontFamily: 'body',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '16px',
            fontWeight: '500',
            height: '36px',
            letterSpacing: '0',
            lineHeight: '1.4',
            paddingBottom: '9px',
            paddingTop: '9px',
            textDecoration: 'none',
          },
          '.mega-menu-drawer__promo a:hover, .mega-menu-drawer__promo a:focus-visible': {
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          },
        },
      },
      drawerChrome: {
        alignItems: 'center',
        bg: 'brandColors.primaryBG',
        borderBottom: 'none',
        columnGap: '24px',
        flexShrink: 0,
        height: 'var(--nav-header-height, 72px)',
        paddingLeft: 'calc(80px + var(--nav-drawer-left, 0px))',
        paddingRight: '80px',
        position: 'relative',
        width: '100%',
        zIndex: 1,
        sx: {
          '.mega-menu-drawer__search-tab--active::after, .mega-menu-drawer__search-tab--active:hover::after': {
            bg: 'currentColor',
            content: '""',
            height: '2px',
          },
        },
      },
      drawerSearchTab: {
        ...noMouseFocusRing,
        alignItems: 'center',
        color: 'brandColors.primaryText',
        display: 'inline-flex',
        flexShrink: 0,
        height: '32px',
        justifyContent: 'center',
        minH: 'unset',
        minW: 'unset',
        opacity: 0.4,
        padding: 0,
        position: 'relative',
        width: '32px',
        _after: {
          bg: 'transparent',
          bottom: 0,
          content: '""',
          height: '2px',
          left: 0,
          position: 'absolute',
          right: 0,
          transition: 'background-color 0.2s ease',
        },
        _hover: {
          bg: 'transparent',
          opacity: 1,
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          opacity: 1,
        },
        sx: {
          svg: {
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
          },
        },
      },
      drawerSearchTabActive: {
        opacity: 1,
        _after: {
          bg: 'currentColor',
          bottom: 0,
          content: '""',
          height: '2px',
          left: 0,
          position: 'absolute',
          right: 0,
        },
        _hover: {
          opacity: 1,
          _after: {
            bg: 'currentColor',
          },
        },
      },
      drawerClose: {
        ...noMouseFocusRing,
        alignItems: 'center',
        color: 'brandColors.primaryText',
        display: 'inline-flex',
        height: '32px',
        justifyContent: 'center',
        minH: 'unset',
        minW: 'unset',
        padding: 0,
        position: 'absolute',
        right: '25px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '32px',
        sx: {
          svg: {
            color: 'brandColors.primaryText',
            height: '20px',
            width: '20px',
          },
        },
      },
      drawerBody: {
        alignItems: 'stretch',
        columnGap: 0,
        flex: '1 1 auto',
        height: '100%',
        minH: 0,
        overflow: 'hidden',
        padding: 0,
      },
      drawerL1Column: {
        alignSelf: 'stretch',
        bg: 'brandColors.primaryBG',
        display: 'flex',
        flex: '0 0 calc(456px + var(--nav-drawer-left, 0px))',
        flexDirection: 'column',
        height: '100%',
        minH: 0,
        overflow: 'hidden',
        width: 'calc(456px + var(--nav-drawer-left, 0px))',
      },
      drawerL1Scroll: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minH: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      },
      drawerL1: {
        flex: '0 0 auto',
        listStyle: 'none',
        margin: 0,
        paddingBottom: '24px',
        paddingLeft: 'calc(80px + var(--nav-drawer-left, 0px))',
        paddingRight: '80px',
        paddingTop: '24px',
      },
      drawerL1Item: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        sx: {
          '.mega-menu-drawer[data-nav-stagger] &': {
            animation: 'navL1FadeIn 240ms cubic-bezier(0.16, 1, 0.3, 1) both',
            animationDelay: 'calc(120ms + min(var(--nav-stagger-index, 0) * 40ms, 240ms))',
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
          },
        },
      },
      drawerL1Button: {
        ...noMouseFocusRing,
        alignItems: 'center',
        color: 'brandColors.itemMenu',
        display: 'flex',
        fontFamily: 'body',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '20px',
        fontWeight: 500,
        gap: '20px',
        letterSpacing: 0,
        height: 'auto',
        justifyContent: 'space-between',
        lineHeight: 1.2,
        minH: {base: '40px', lg: '32px'},
        minW: 0,
        px: 0,
        py: '8px',
        textAlign: 'left',
        width: '100%',
        _hover: {
          opacity: 1,
          textDecoration: 'none',
          svg: {
            color: 'brandColors.greyscale',
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          svg: {
            color: 'brandColors.greyscale',
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        sx: {
          span: {
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            lineHeight: 'inherit',
          },
          svg: {
            color: 'brandColors.greyscale',
            flexShrink: 0,
            height: '11.25px',
            opacity: 0,
            transform: 'translateX(-6px)',
            transition: 'opacity 0.25s cubic-bezier(0.22, 1, 0.36, 1), transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
            width: '5.47px',
            path: {
              opacity: '1',
            },
          },
          '@media (prefers-reduced-motion: reduce)': {
            svg: {
              transition: 'none',
            },
          },
        },
      },
      drawerL1ButtonSelected: {
        color: 'brandColors.itemMenu',
        opacity: 1,
        sx: {
          svg: {
            color: 'brandColors.greyscale',
            height: '11.25px',
            opacity: 1,
            transform: 'translateX(0)',
            width: '5.47px',
            path: {
              opacity: '1',
            },
          },
        },
      },
      drawerL1ButtonMuted: {
        opacity: 0.4,
        _hover: {
          opacity: 1,
          svg: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          opacity: 1,
          svg: {
            opacity: 1,
            transform: 'translateX(0)',
          },
        },
      },
      drawerPromoGroup: {
        borderTop: '1px solid',
        borderColor: 'brandColors.greyscale10',
        display: 'flex',
        flexDirection: 'column',
        listStyle: 'none',
        margin: 0,
        paddingBottom: '20px',
        paddingLeft: 'calc(80px + var(--nav-drawer-left, 0px))',
        paddingRight: '80px',
        paddingTop: '21px',
        sx: {
          '.mega-menu-drawer[data-nav-stagger] & > li': {
            animation: 'navL1FadeIn 240ms cubic-bezier(0.16, 1, 0.3, 1) both',
            animationDelay: 'calc(120ms + min(var(--nav-stagger-index, 0) * 40ms, 240ms))',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& > li': {
              animation: 'none',
            },
          },
        },
      },
      drawerPromoLink: {
        ...noMouseFocusRing,
        alignItems: 'center',
        color: 'brandColors.itemMenu',
        display: 'flex',
        fontFamily: 'body',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '16px',
        fontWeight: 500,
        height: '36px',
        letterSpacing: 0,
        lineHeight: 1.4,
        minW: 0,
        px: 0,
        py: '9px',
        textDecoration: 'none',
        _hover: {
          color: 'brandColors.itemMenu',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _visited: {
          color: 'brandColors.itemMenu',
        },
      },
      drawerL2: {
        alignSelf: 'stretch',
        bg: 'rgba(255, 255, 255, 0.3)',
        borderLeft: '1px solid',
        borderColor: 'brandColors.greyscale10',
        display: 'flex',
        flex: '0 0 320px',
        flexDirection: 'column',
        gap: '24px',
        minH: 0,
        h: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        padding: '96px 40px',
        WebkitOverflowScrolling: 'touch',
        width: '320px',
      },
      drawerShopAll: {
        ...noMouseFocusRing,
        alignItems: 'center',
        color: 'brandColors.primaryText',
        display: 'inline-flex',
        flexShrink: 0,
        fontFamily: 'body',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '20px',
        fontWeight: 500,
        gap: '12px',
        height: '36px',
        letterSpacing: 0,
        lineHeight: 1.2,
        w: 'max-content',
        _hover: {
          textDecoration: 'none',
          '& > span:first-of-type': {
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          },
          '& svg': {
            transform: 'translateX(4px)',
          },
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          '& > span:first-of-type': {
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          },
        },
        sx: {
          alignItems: 'center',
          display: 'inline-flex',
          width: 'max-content',
          svg: {
            color: 'brandColors.primaryText',
            display: 'block',
            flexShrink: 0,
            height: '16px',
            transition: 'transform 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
            width: '16px',
          },
          '@media (prefers-reduced-motion: reduce)': {
            svg: {
              transition: 'none',
            },
          },
        },
      },
      drawerL2Column: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      },
      drawerL2LinkGroup: {
        display: 'flex',
        flexDirection: 'column',
        sx: {
          '&:hover > *, &:focus-within > *': {
            opacity: 0.4,
            transition: 'opacity 150ms ease',
          },
          '&:hover > *:hover, &:focus-within > *:focus-visible': {
            opacity: 1,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&:hover > *, &:focus-within > *': {
              transition: 'none',
            },
          },
        },
      },
      drawerL2ColumnTitle: {
        color: '#8D8C8B',
        fontFamily: 'body',
        fontSize: '12px',
        fontWeight: 400,
        letterSpacing: 0,
        lineHeight: 1.2,
        textTransform: 'none',
      },
      drawerL2Link: {
        ...noMouseFocusRing,
        color: 'brandColors.primaryText',
        display: 'flex',
        fontFamily: 'body',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '20px',
        fontWeight: 500,
        letterSpacing: 0,
        lineHeight: 1.2,
        minH: {base: '40px', lg: '32px'},
        minW: 0,
        px: 0,
        py: '8px',
        _hover: {
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
      },
      drawerGallery: {
        alignContent: 'start',
        alignSelf: 'stretch',
        bg: 'rgba(255, 255, 255, 0.3)',
        display: 'grid',
        flex: '1 1 auto',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 320px), 1fr))',
        gap: '24px 8px',
        justifyItems: 'stretch',
        minH: 0,
        minW: 0,
        h: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: '96px',
        paddingTop: '96px',
        paddingX: '8px',
      },
      drawerGalleryItem: {
        ...noMouseFocusRing,
        animation: 'navL3TileIn 360ms cubic-bezier(0.16, 1, 0.3, 1) backwards',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxW: '472px',
        minW: 0,
        textDecoration: 'none',
        w: '100%',
        _focusVisible: {
          ...A11Y_FOCUS_VISIBLE._focusVisible,
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _hover: {
          img: {
            transform: 'scale(1.04)',
          },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
          _hover: {
            img: {
              transform: 'none',
            },
          },
        },
      },
      drawerGalleryImage: {
        aspectRatio: '1 / 1',
        bg: 'brandColors.primaryLinen15',
        overflow: 'hidden',
        sx: {
          '> div': {
            aspectRatio: '1 / 1',
          },
          img: {
            display: 'block',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 300ms cubic-bezier(0.16, 1, 0.3, 1)',
            width: '100%',
          },
          '@media (prefers-reduced-motion: reduce)': {
            img: {
              transition: 'none',
            },
          },
        },
      },
      drawerGalleryCaption: {
        color: 'brandColors.itemMenu',
        display: 'block',
        fontFamily: 'body',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '16px',
        fontWeight: 500,
        letterSpacing: 0,
        lineHeight: 1.4,
      },
      drawerSearchBody: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        minH: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        paddingBottom: '40px',
        paddingLeft: 'calc(80px + var(--nav-drawer-left, 0px))',
        paddingRight: '40px',
        paddingTop: '40px',
        WebkitOverflowScrolling: 'touch',
      },
      megaMenuDesktopNav: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: '56px', // Reserve space to prevent shift when loading
        paddingBottom: '0',
        borderBottom: '1px solid',
        // Border color based on header state:
        // - transparent or solid: brandColors.greyscale20
        // - gradient: whiteAlpha.450
        // When hovering header, gradient/transparent transform to solid (keeps greyscale20)
        borderColor: headerState === 'gradient' ?
          'whiteAlpha.450' :
          'brandColors.greyscale20',
        transition: 'border-color 0.2s ease',
        pointerEvents: isSearchOpen ? 'none' : 'auto',
      },
      menuItemDesktopContainer: {
        maxWidth: '1440px',
        mx: 'auto',
        paddingX: {base: '20px', lg: '80px'},
        paddingTop: {base: '0', lg: '45px'},
        paddingBottom: {base: '0', lg: '84px'},
        width: '100%',
      },
      // Desktop mega-menu: 5-col CSS Grid — nav-wrapper + one cell per image.
      menuItemDesktopGrid: {
        alignItems: 'start',
        display: 'grid',
        gap: 6,
        gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
        width: '100%',
      },
      navWrapper: {
        alignItems: 'start',
        display: 'grid',
        gap: 'inherit',
        minWidth: 0,
        width: '100%',
        sx: {
          '& > .navigation-column': {
            marginBottom: 0,
            minWidth: 0,
            width: '100%',
          },
        },
      },
      menuItemDesktopImage: {
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        minWidth: 0,
        width: '100%',
        sx: {
          '& .chakra-link, & img, & .chakra-image': {
            display: 'block',
            maxWidth: '100%',
            width: '100%',
          },
          '& .chakra-text': {
            color: 'greyscale90Alpha.90',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '16px',
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: 1.2,
            transition: 'color 0.2s ease',
          },
          '& .chakra-link': {
            borderRadius: '2px',
            cursor: 'pointer',
            display: 'block',
            transition: 'opacity 0.2s ease',
            _focusVisible: {
              boxShadow: 'none',
              outline: '2px solid',
              outlineColor: 'brandColors.primaryDarkLinen',
              outlineOffset: '2px',
            },
          },
          '& .chakra-link:hover .chakra-image, & .chakra-link:hover img': {
            opacity: 0.85,
          },
          '&:has(.chakra-link):hover .image__description, &:has(.chakra-link):hover .image__description .chakra-text, &:has(.chakra-link):hover > .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
          '& .chakra-link:focus-visible ~ .image__description, & .chakra-link:focus-visible ~ .image__description .chakra-text, & .chakra-link:focus-visible ~ .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
        },
      },
      desktopTriggerButton: {
        // Color comes from variant (light/dark)
        transition: 'color 0.3s ease',
        _hover: {
          opacity: 0.7,
          _after: {
            bg: 'brandColors.primaryDarkLinen', // Show dark underline on hover for all items
          },
        },
        alignItems: 'center',
        display: 'flex',
        fontSize: '16px',
        fontWeight: '500',
        position: 'relative',
        textDecoration: 'none', // Prevent link underline
        // Active indicator pseudo-element
        _after: {
          content: '""',
          position: 'absolute',
          bottom: '-17px',
          left: 0,
          right: 0,
          height: '2px',
          width: 'calc(100% + 10px)',
          bg: isOpen ? 'brandColors.primaryDarkLinen' : 'transparent',
          transition: 'background-color 0.2s ease',
        },
        sx: {
          '.menu-item-wrapper .chakra-link': {
            bg: 'transparent',
            border: 'none',
            borderRadius: 0,
            color: 'inherit',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            p: 0,
            _hover: {
              bg: 'transparent',
              border: 'none',
              color: 'inherit',
              textDecoration: 'none',
            },
          },
          '.chakra-link': {
            _hover: {
              textDecoration: 'none',
            },
          },
        },
      },
      desktopPopoverContent: {
        width: '100vw',
        left: '0',
        right: '0',
        marginLeft: '0',
        marginRight: '0',
        marginTop: '0',
        paddingTop: '0',
        padding: 0,
        transform: 'none',
        // Above the global blur overlay (1590). Search modal is 1600 — drop behind it when search is open.
        zIndex: isSearchOpen ? 1 : 1600,
        pointerEvents: isSearchOpen ? 'none' : 'auto',
        position: 'fixed',
        // Dynamic top positioning based on trigger element's bottom
        top: isDesktop && popoverTop > 0 ? `${popoverTop}px` : 'auto',
        // Remove default Chakra popover styles that cause flash
        bg: 'background.primary',
        boxShadow: 'none',
        border: 'none',
        borderRadius: 0,
      },

      megaMenuLoadingSpinner: {
        color: 'brandColors.primaryDarkLinen',
        size: 'md',
      },

      saleBadge: {
        display: 'inline-block',
        bg: '#BF1619',
        borderRadius: 'md',
        height: '10px',
        width: '10px',
        marginRight: '13px',
      },

      // Mobile drawer header / body
      mobileDrawerHeader: {
        alignItems: 'center',
        borderBottom: 'none',
        boxShadow: 'none',
        display: 'flex',
        height: '56px',
        justifyContent: 'space-between',
        padding: '8px 16px',
        position: 'relative',
      },
      mobileDrawerHeaderL2: {
        borderBottom: '1px solid',
        borderColor: 'brandColors.greyscale10',
      },
      mobileDrawerTitle: {
        color: 'brandColors.itemMenu',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '20px',
        fontWeight: 500,
        left: '50%',
        lineHeight: 1.5,
        margin: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        whiteSpace: 'nowrap',
      },
      mobileDrawerOverlay: {
        zIndex: 1098,
      },
      mobileDrawerContent: {
        bg: 'brandColors.primaryBG',
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        overflow: 'hidden',
        zIndex: 1099,
      },
      closeButton: {
        alignItems: 'center',
        display: 'inline-flex',
        flexShrink: 0,
        height: '32px',
        justifyContent: 'center',
        minHeight: 'unset',
        minWidth: 'unset',
        position: 'relative',
        width: '32px',
        zIndex: 1,
        sx: {
          svg: {
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
          },
        },
      },
      accountNameBadge: {
        bg: 'white',
        borderRadius: '50px',
        color: 'brandColors.greyscale80',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: A11Y_ICON_TARGET_MIN,
        height: A11Y_ICON_TARGET_MIN,
        fontSize: '11px',
        fontWeight: 500,
      },
      accountNameBadgeNotLoggedIn: {
        bg: 'transparent',
        width: 'auto',
        gap: '16px',
        sx: {
          '.chakra-button': {
            gap: '16px',
            width: 'auto',
          },
          '.chakra-button__icon': {
            marginRight: '0',
            width: A11Y_ICON_TARGET_MIN,
          },
          '.chakra-icon': {
            color: 'brandColors.primaryDarkLinen',
            height: A11Y_ICON_GLYPH_SIZE,
            width: A11Y_ICON_GLYPH_SIZE,
          },
        }
      },
      logoMobile: {
        width: '100%',
        height: 'auto',
      },
      logoMobileWrapper: {
        display: 'flex',
        height: '16px',
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: '75px',
        zIndex: 1,
      },
      mobileDrawerLogoButton: {
        minWidth: 'auto',
        padding: 0,
      },
      mobileSearch: {
        flexShrink: 0,
        px: '16px',
        py: '8px',
        width: '100%',
      },
      mobileSearchInput: {
        bg: 'whiteAlpha.300',
        border: '1px solid',
        borderColor: 'brandColors.primaryText90',
        borderRadius: '4px',
        boxSizing: 'border-box',
        color: 'brandColors.greyscale',
        fontSize: '14px',
        fontWeight: 400,
        height: '44px',
        letterSpacing: 0,
        lineHeight: 1.4,
        paddingLeft: '16px',
        paddingRight: '44px',
        _placeholder: {
          color: 'brandColors.greyscale',
          opacity: 0.5,
        },
        _focus: {
          borderColor: 'brandColors.primaryText90',
          boxShadow: 'none',
        },
        _focusVisible: {
          borderColor: 'brandColors.greyscale',
          boxShadow: 'none',
          outline: '1px solid',
          outlineColor: 'brandColors.greyscale',
          outlineOffset: '2px',
        },
      },
      mobileSearchIcon: {
        color: 'brandColors.greyscale',
        display: 'inline-flex',
        sx: {
          svg: {
            height: '16px',
            width: '16px',
          },
        },
      },
      mobileTabsRow: {
        borderBottom: '1px solid',
        borderColor: 'brandColors.greyscale10',
        flexShrink: 0,
        px: '20px',
        width: '100%',
        sx: {
          '.header-primary-nav': {
            columnGap: '24px',
          },
          '.header-primary-nav__item': {
            color: 'brandColors.primaryText90',
            minHeight: 'unset',
            opacity: '1 !important',
            py: '10px',
          },
        },
      },
      mobileDrawerBody: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        overscrollBehavior: 'contain',
        padding: '0',
        position: 'relative',
      },
      mobileDrawerBodyInner: {
        flex: '1 1 auto',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
        willChange: 'transform',
      },
      mobileL1Slide: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        left: 0,
        minHeight: 0,
        overflow: 'hidden',
        position: 'absolute',
        top: 0,
        transition: `transform ${NAV_MOBILE_L2_SLIDE_MS}ms ease-in-out`,
        width: '100%',
        willChange: 'transform',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      },
      mobileL1Panel: {
        flex: '1 1 auto',
        height: 'auto',
        left: 'auto',
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        top: 'auto',
        transition: 'none',
        width: '100%',
      },
      mobileSubcategoryPanel: {
        bg: 'brandColors.primaryBG',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        height: '100%',
        minHeight: 0,
        left: 0,
        position: 'absolute',
        top: 0,
        transition: `transform ${NAV_MOBILE_L2_SLIDE_MS}ms ease-in-out`,
        width: '100%',
        willChange: 'transform',
        boxSizing: 'border-box',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      },
      mobileDrawerLocaleSelector: {
        sx: {
          '.chakra-button': {
            width: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          },
          // Globe icon: primary dark linen; language label uses itemMenu.
          '& .chakra-button svg': {
            color: 'brandColors.primaryDarkLinen',
          },
          // Language code matches footer row typography.
          '.chakra-button .chakra-text': {
            color: 'brandColors.itemMenu',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '1.2',
          },
        },
      },
      mobileSubcategoryPanelContent: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        px: '20px',
        sx: {
          WebkitOverflowScrolling: 'touch',
        },
      },
      mobileSubcategoryPanelWrapper: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        width: '100%',
        sx: {
          WebkitOverflowScrolling: 'touch',
        },
      },
      mobileSubcategoryPanelWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        pb: '80px',
        pt: '24px',
        px: '20px',
        width: '100%',
      },
      mobileSubmenuItemsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      },
      mobileSubmenuItem: {
        paddingRight: 0,
      },
      mobilePanelItemsList: {
        padding: '20px 0 0',
        spacing: 0,
        width: '100%',
      },
      mobileMenuItemText: {
        color: 'brandColors.itemMenu',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '18px',
        fontWeight: 500,
        lineHeight: 1.2,
      },
      mobilePromoGroup: {
        borderTop: '1px solid',
        borderColor: 'brandColors.greyscale10',
        display: 'flex',
        flexDirection: 'column',
        ml: '-20px',
        mr: '-20px',
        mt: '4px',
        pb: '16px',
        pl: '20px',
        pr: '20px',
        pt: '17px',
        width: 'calc(100% + 40px)',
      },
      mobilePromoLink: {
        alignItems: 'center',
        color: 'brandColors.itemMenu',
        display: 'flex',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        height: '36px',
        letterSpacing: 0,
        lineHeight: 1.4,
        minW: 0,
        py: '9px',
        textDecoration: 'none',
        _hover: {
          color: 'brandColors.itemMenu',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _focusVisible: {
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
      },
      mobileCtaFooter: {
        backdropFilter: 'blur(20px)',
        bg: 'rgba(247, 245, 240, 0.66)',
        borderTop: '1px solid',
        borderColor: 'brandColors.greyscale10',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        gap: '12px',
        justifyContent: 'flex-end',
        padding: '12px 20px calc(20px + env(safe-area-inset-bottom))',
        width: '100%',
      },
      mobileCtaRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '12px',
        width: '100%',
      },
      mobileCtaExtraRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: '100%',
      },
      mobileCtaButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'brandColors.primaryText90',
        borderRadius: '4px',
        color: 'brandColors.itemMenu',
        display: 'inline-flex',
        flex: '1 1 0',
        fontFamily: 'inherit',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        gap: '8px',
        height: '40px',
        justifyContent: 'center',
        letterSpacing: 0,
        lineHeight: 1.4,
        maxWidth: '100%',
        minW: 0,
        overflow: 'hidden',
        padding: '0 16px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        _hover: {
          bg: 'white',
          textDecoration: 'none',
        },
        _active: {
          bg: 'white',
        },
        _focusVisible: {
          bg: 'white',
          boxShadow: '0 0 0 3px #1B96FF',
          outline: 'none',
        },
        sx: {
          svg: {
            flexShrink: 0,
            height: '20px',
            width: '20px',
          },
          img: {
            flexShrink: 0,
            height: '20px',
            width: '20px',
          },
          span: {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.4,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      mobileCtaAccountBadge: {
        alignItems: 'center',
        bg: 'transparent',
        border: '1.5px solid',
        borderColor: 'currentColor',
        borderRadius: 'full',
        color: 'inherit',
        display: 'inline-flex',
        flexShrink: 0,
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '10px',
        fontWeight: 500,
        height: '20px',
        justifyContent: 'center',
        letterSpacing: 0,
        lineHeight: 1.4,
        width: '20px',
      },
      // Size/spacing only — bg, color, border come from BaseLink Type variants.
      mobileCtaTypedLink: {
        alignItems: 'center',
        borderRadius: '4px',
        display: 'inline-flex',
        flexShrink: 0,
        fontFamily: 'inherit',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        gap: '8px',
        height: '40px',
        justifyContent: 'center',
        letterSpacing: 0,
        lineHeight: 1.4,
        maxWidth: '100%',
        minW: 0,
        overflow: 'hidden',
        padding: '0 16px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        width: '100%',
        _hover: {
          textDecoration: 'none',
        },
        _focusVisible: {
          boxShadow: '0 0 0 3px #1B96FF',
          outline: 'none',
        },
        sx: {
          svg: {
            flexShrink: 0,
            height: '20px',
            width: '20px',
          },
          img: {
            flexShrink: 0,
            height: '20px',
            width: '20px',
          },
          span: {
            color: 'inherit',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.4,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
      },
      mobileCtaExtraLink: {
        alignItems: 'center',
        color: 'brandColors.itemMenu',
        display: 'flex',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        height: '36px',
        justifyContent: 'flex-start',
        letterSpacing: 0,
        lineHeight: 1.4,
        minW: 0,
        py: '9px',
        textDecoration: 'none',
        _hover: {
          color: 'brandColors.itemMenu',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
        _focusVisible: {
          boxShadow: '0 0 0 3px #1B96FF',
          outline: 'none',
          textDecoration: 'underline',
          textUnderlineOffset: '3px',
        },
      },

      mobileDrawerFooter: {
        padding: '20px 0 calc(4px + env(safe-area-inset-bottom)) 0', // Add safe area padding
        flexShrink: 0, // Prevent footer from shrinking
        marginTop: 'auto', // Push footer to bottom
      },

      mobileDrawerFooterStack: {
        alignItems: 'flex-start',
        spacing: 0,
      },
      mobileDrawerFooterRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        columnGap: '16px',
        sx: {
          '.chakra-button': {
            height: A11Y_ICON_TARGET_MIN,
            width: A11Y_ICON_TARGET_MIN,
            minHeight: 'unset',
            minWidth: 'unset',
          },
          '.account-name-badge-button': {
            gap: '16px',
            width: 'auto',
          },
          '.mobile-drawer-footer-text': {
            color: 'brandColors.itemMenu',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '150%',
          },
        }
      },
      mobileDrawerFooterIconButton: {
        alignItems: 'center',
        color: 'brandColors.primaryDarkLinen',
        display: 'inline-flex',
        height: A11Y_ICON_TARGET_MIN,
        justifyContent: 'center',
        minHeight: 'unset',
        minWidth: 'unset',
        width: A11Y_ICON_TARGET_MIN,
        lineHeight: '120%',
        sx: {
          '& svg': {
            width: A11Y_ICON_GLYPH_SIZE,
            height: A11Y_ICON_GLYPH_SIZE,
          },
          '&.account-name-badge': {
            bg: 'whiteAlpha.800',
            color: 'brandColors.greyscale80',
            fontSize: '11px',
          }
        }
      },
      mobileMenuItemParentWrapper: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        color: 'brandColors.itemMenu',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '18px',
        gap: '20px',
        height: '44px',
        lineHeight: 1.2,
        padding: '10px 16px 10px 0',
        textAlign: 'left',
        textDecoration: 'none',
        width: '100%',
        _hover: {
          textDecoration: 'none',
        },
      },

      menuItemIconButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        display: 'flex',
        flexShrink: 0,
        height: '13px',
        justifyContent: 'center',
        p: 0,
        width: '7px',
      },
      // Shared styles
      menuItemLink: {
        alignItems: 'center',
        color: 'brandColors.greyscale90',
        justifyContent: 'space-between',
        textDecoration: 'none',
        width: '100%',
        _hover: {
          color: 'brandColors.primaryDarkLinen',
          textDecoration: 'underline',
        },
      },

      menuItemButton: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        cursor: 'pointer',
        justifyContent: 'space-between',
        p: 0,
        textAlign: 'left',
        width: '100%',
      },

      menuItemText: {
        ...getTextStyles(),
        flex: '1 0 0',
        minWidth: 0,
        wordBreak: 'break-word',
      },

      menuItemContainer: {
        marginBottom: {base: 0, lg: '32px'},
      },

      menuItemIcon: {
        boxSize: 'auto',
        color: '#1B1A18',
        flexShrink: 0,
        height: '13px',
        width: '7px',
      },

      menuItemImages: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: {base: '4px', lg: '36px'},
        mt: 0,
        overflowX: {base: 'auto', lg: 'visible'},
        sx: {
          '.menu-item-image': {
            minWidth: {base: '258px', lg: 'auto'},
          },
          '&.menu-item-images__mobile': {
            marginLeft: '-20px',
            marginRight: '-20px',
            marginTop: 0,
            paddingBottom: '2px',
            paddingLeft: '20px',
            scrollPaddingLeft: '20px',
            width: 'calc(100% + 40px)',
          },
          '&.menu-item-images__mobile .menu-item-image': {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: '14px',
            width: '258px',
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-link, &.menu-item-images__mobile .menu-item-image img, &.menu-item-images__mobile .menu-item-image .chakra-image': {
            display: 'block',
            height: 'auto',
            objectFit: 'cover',
            width: '100%',
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-text, &.menu-item-images__mobile .menu-item-image [class*="description"]': {
            marginTop: 0,
            paddingX: 0,
          },
          '&.menu-item-images__desktop': {
            marginLeft: 'auto',
          },
          '&.menu-item-images__desktop .menu-item-image': {
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            gap: '14px',
            width: {lg: '258px'},
          },
          '&.menu-item-images__desktop .menu-item-image .chakra-link, .menu-item-images__desktop .menu-item-image img, .menu-item-images__desktop .menu-item-image .chakra-image': {
            display: {lg: 'block'},
            width: {lg: '100%'},
          },
          '&.menu-item-images__mobile .menu-item-image .chakra-text': {
            color: 'brandColors.greyscale90',
            fontSize: '18px',
            fontWeight: 500,
            transition: 'color 0.2s ease',
          },
          '&.menu-item-images__desktop .menu-item-image .chakra-text': {
            color: 'greyscale90Alpha.90',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: '16px',
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: 1.2,
            transition: 'color 0.2s ease',
          },
          // Linked promo tiles — match nav link hover (primaryDarkLinen); no layout shift (opacity/outline only).
          '& .menu-item-image .chakra-link': {
            borderRadius: '2px',
            cursor: 'pointer',
            display: 'block',
            transition: 'opacity 0.2s ease',
            _focusVisible: {
              boxShadow: 'none',
              outline: '2px solid',
              outlineColor: 'brandColors.primaryDarkLinen',
              outlineOffset: '2px',
            },
          },
          '& .menu-item-image .chakra-link:hover .chakra-image, & .menu-item-image .chakra-link:hover img': {
            opacity: 0.85,
          },
          '& .menu-item-image:has(.chakra-link):hover .image__description, & .menu-item-image:has(.chakra-link):hover .image__description .chakra-text, & .menu-item-image:has(.chakra-link):hover > .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
          '& .menu-item-image .chakra-link:focus-visible ~ .image__description, & .menu-item-image .chakra-link:focus-visible ~ .image__description .chakra-text, & .menu-item-image .chakra-link:focus-visible ~ .chakra-text': {
            color: 'brandColors.primaryDarkLinen',
          },
        },
      },
      // Navigation column styles
      navigationColumnWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: {base: '12px', lg: '16px'},
        marginBottom: {base: 0, lg: '16px'},
        minWidth: {base: 'auto', lg: '160px'},
        width: {base: '100%', lg: 'auto'},
      },
      navigationColumnLinksWrapper: {
        display: 'flex',
        flexDirection: 'column',
        gap: {base: 0, lg: '16px'},
        sx: {
          '& .chakra-link.link--outlined-light, & .chakra-link.link--filled-light, & .chakra-link.link--outlined-dark, & .chakra-link.link--filled-dark': {
            width: 'fit-content',
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark), .chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark) > .chakra-text': {
            color: 'brandColors.itemMenu',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: {base: '18px', lg: '14px'},
            fontWeight: 500,
            lineHeight: 1.2,
            textDecoration: 'none',
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark)': {
            alignItems: 'center',
            display: 'flex',
            minHeight: {base: '40px', lg: 'unset'},
            py: {base: '4px', lg: 0},
            width: '100%',
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):hover, .chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):hover > .chakra-text': {
            color: {base: 'brandColors.itemMenu', lg: 'brandColors.primaryDarkLinen'},
            textDecoration: {base: 'underline', lg: 'none'},
            textUnderlineOffset: {base: '3px', lg: 'unset'},
          },
          '.chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):focus-visible, .chakra-link:not(.link--outlined-light):not(.link--filled-light):not(.link--outlined-dark):not(.link--filled-dark):focus-visible > .chakra-text': {
            textDecoration: {base: 'underline', lg: 'none'},
            textUnderlineOffset: {base: '3px', lg: 'unset'},
          },
          '.link--outlined-light:hover .chakra-text, .link--filled-light:hover .chakra-text, .link--outlined-dark:hover .chakra-text, .link--filled-dark:hover .chakra-text': {
            color: 'inherit',
          },
        },
      },
      navigationColumnTitle: {
        color: {base: 'brandColors.greyscale50', lg: 'brandColors.primaryDarkLinen'},
        fontSize: {base: '12px', lg: '10px'},
        fontWeight: {base: 400, lg: 600},
        letterSpacing: {base: 0, lg: '2px'},
        lineHeight: {base: 1.2, lg: 'normal'},
        textTransform: {base: 'none', lg: 'uppercase'},
        width: '100%',
      },

      navigationColumnTitleSkeleton: {
        borderRadius: '2px',
      },

      navigationColumnLinkSkeleton: {
        borderRadius: '2px',
      },
      backButton: {
        color: 'brandColors.itemMenu',
        fontFeatureSettings: "'lnum' 1, 'pnum' 1",
        fontSize: '14px',
        fontWeight: 500,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      },
      backButtonIcon: {
        boxSize: 'auto',
        color: 'brandColors.primaryText',
        flexShrink: 0,
        height: '13px',
        width: '8px',
      },
      backButtonWrapper: {
        alignItems: 'center',
        bg: 'transparent',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-start',
        padding: 0,
        width: 'auto',
      },
      shopAllLinkWrapper: {
        marginBottom: {base: 0, lg: '0'},
        marginTop: {base: 0, lg: '40px'},
        sx: {
          'a': {
            alignItems: 'center',
            color: {base: 'brandColors.itemMenu', lg: 'black'},
            display: 'flex',
            fontFeatureSettings: "'lnum' 1, 'pnum' 1",
            fontSize: {base: '18px', lg: '14px'},
            fontWeight: {base: 500, lg: 600},
            gap: {base: '8px', lg: '12px'},
            height: {base: '36px', lg: 'auto'},
            lineHeight: 1.2,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            _hover: {
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
            _focusVisible: {
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            },
          },
          'a span, a .chakra-text': {
            fontSize: 'inherit',
            fontWeight: 'inherit',
            letterSpacing: 'inherit',
            lineHeight: 'inherit',
          },
          '.chakra-icon': {
            color: {base: 'brandColors.itemMenu', lg: 'brandColors.primaryDarkLinen'},
            height: {base: '16px', lg: '16px'},
            width: {base: '16px', lg: '16px'},
          },
        },
      },
      shopAllTitleIcon: {
        color: 'brandColors.itemMenu',
        flexShrink: 0,
        height: '16px',
        width: '16px',
      },
    };
  },

  /**
   * Variants for nav item color scheme (shared with Header)
   * - light: Dark nav items (for transparent, solid states)
   * - dark: White nav items (for gradient state)
   */
  variants: {
    light: getNavVariantStyles('brandColors.greyscale90'),
    dark: getNavVariantStyles('text.white'),
    drawer: getNavVariantStyles('brandColors.primaryText90'),
  },

  /**
   * Default variant (light = dark text)
   */
  defaultProps: {
    variant: 'light',
  },
};

export default MegaMenu;
